/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import _has from "lodash/has";
import _merge from "lodash/merge";
import * as applicationStatuses from "./applicationStatus";
import formSteps from "./formSteps";
import { getProductData } from "@/src/services/productService";
import _includes from "lodash/includes";
import {
  fromBoolean,
  fromDate,
  fromFloat,
  fromInsurer,
  fromNumber,
  getConvertedFormDataBase,
  hasChangedData,
  pickAndConvert,
  prepareFormDataBase,
  sanitizeAddressData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toFloat,
  toInsurer,
  toNumber,
} from "@/src/services/formServiceFunctions";
import ApiClient from "@/src/services/apiClient";
import { FormServiceBase, FormServiceSaveProps } from "@/src/types/FormServiceBase";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { Customer } from "@/src/types/Entities/Customer";
import _pick from "lodash/pick";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (product: AbstractCase): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_BASE]: {
      [product.name]: [
        "caseCategory",
        "initialClientDescriptionAndObjectives",
        "clientWasMinorAtContractDate",
        "hasReceivedAdministrativeDecision",
        "administrativeDecisionReceivedDate",
        "hasFiledObjectionToAdministrativeDecision",
        "extrajudicialCommunicationSent",
        "clientLegalRepresentativeAmount",
        "clientLegalRepresentativesAuthorizationType",
        "civilServiceStatus",
      ],
      client: ["gender", "givenName", "familyName", "birthDate"],
      "client.bankData": ["iban"],
      "client.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      clientLegalRepresentative: ["gender", "givenName", "familyName"],
      "clientLegalRepresentative.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      secondClientLegalRepresentative: ["gender", "givenName", "familyName"],
      "secondClientLegalRepresentative.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      customer: ["telephone"],
      "customer.person": ["gender", "givenName", "familyName"],
      "customer.person.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      additionalClients: [],
    },
    [applicationStatuses.APPLICATION_STATUS_EMPLOYMENT_RELATIONSHIP]: {
      [product.name]: [
        "employerType",
        "employerState",
        "employerMunicipality",
        "authorityName",
        "authorityTelephone",
        "personnelNumber",
        "objectiveClient",
        "workLocationPostalCode",
      ],
      authorityAddress: ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_REFINEMENT]: {
      [product.name]: ["refinementAnswers"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONCERN_SUMMARY]: {
      [product.name]: ["finalClientDescriptionAndObjectives"],
    },
    [applicationStatuses.APPLICATION_STATUS_DOCUMENTS]: {},
    [applicationStatuses.APPLICATION_STATUS_INSURANCE]: {
      caseInsurance: ["insurer", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType", "deductible"],
      "caseInsurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      [product.name]: ["powerOfAttorney"],
    },
  });

  conversionFields = [
    {
      path: "client.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "customer.person.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "administrativeDecisionReceivedDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "extrajudicialCommunicationSent",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "clientWasMinorAtContractDate",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "hasReceivedAdministrativeDecision",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "hasFiledObjectionToAdministrativeDecision",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "legalExpenseInsurance",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "caseInsurance.deductible",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "clientLegalRepresentativeAmount",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "caseInsurance.insurer",
      convertFrom: fromInsurer,
      convertTo: toInsurer,
    },
  ];

  save = async ({
    formData,
    applicationStatus,
    nextApplicationStatus,
    product,
    customer,
    updateMutation,
    refreshCustomer,
  }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields(product)[applicationStatus] || {};
    const convertedFormData = getConvertedFormDataBase(formData, this.conversionFields);

    let hasCustomerUpdates = false;
    let clearGenerationFields = false;

    if (_has(fields, "customer.person.residenceAddress") && !!customer.person?.residenceAddress?.id) {
      let addressData = pickAndConvert(formData.customer.person.residenceAddress, fields["customer.person.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);

      if (hasChangedData(addressData, customer.person.residenceAddress)) {
        hasCustomerUpdates = true;
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: customer.person.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "customer.person") && !!customer.person?.id) {
      const convertedFormData = this.getConvertedFormData(formData);
      let customerPersonData = pickAndConvert(convertedFormData.customer.person, fields["customer.person"]);

      customerPersonData = sanitizePersonData(customerPersonData);

      if (hasChangedData(customerPersonData, customer.person)) {
        hasCustomerUpdates = true;
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/people",
          id: customer.person.id,
          data: customerPersonData,
        });
      }
    }

    if (_has(fields, "customer")) {
      const customerData = pickAndConvert(formData.customer, fields.customer);

      if (hasChangedData(customerData, product.customer)) {
        hasCustomerUpdates = true;
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/customers",
          id: customer.id,
          data: customerData,
        });
      }
    }

    if (_has(fields, "client.residenceAddress")) {
      const baseAddressData = formData.affectedPartyType === "customer" ? formData.customer.person.residenceAddress : formData.client.residenceAddress;
      let addressData = pickAndConvert(baseAddressData, fields["client.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);

      if (hasChangedData(addressData, product.client.residenceAddress)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.client.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "client")) {
      const convertedFormData = this.getConvertedFormData(formData);
      const baseClientData = convertedFormData.affectedPartyType === "customer" ? convertedFormData.customer.person : convertedFormData.client;
      let clientData = pickAndConvert(baseClientData, fields.client);
      clientData = sanitizePersonData(clientData);

      if (hasChangedData(clientData, product.client)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.client.id,
          data: clientData,
        });
      }
    }

    if (_has(fields, "clientLegalRepresentative")) {
      let clientLegalRepresentativeData = pickAndConvert(formData.clientLegalRepresentative, fields.clientLegalRepresentative);
      clientLegalRepresentativeData = sanitizePersonData(clientLegalRepresentativeData);

      if (hasChangedData(clientLegalRepresentativeData, product.clientLegalRepresentative)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.clientLegalRepresentative.id,
          data: clientLegalRepresentativeData,
        });
      }
    }

    if (_has(fields, "clientLegalRepresentative.residenceAddress")) {
      let addressData = pickAndConvert(formData.clientLegalRepresentative.residenceAddress, fields["clientLegalRepresentative.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);

      if (hasChangedData(addressData, product.clientLegalRepresentative.residenceAddress)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.clientLegalRepresentative.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "secondClientLegalRepresentative")) {
      let secondClientLegalRepresentativeData = pickAndConvert(formData.secondClientLegalRepresentative, fields.secondClientLegalRepresentative);
      secondClientLegalRepresentativeData = sanitizePersonData(secondClientLegalRepresentativeData);

      if (hasChangedData(secondClientLegalRepresentativeData, product.secondClientLegalRepresentative)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.secondClientLegalRepresentative.id,
          data: secondClientLegalRepresentativeData,
        });
      }
    }

    if (_has(fields, "secondClientLegalRepresentative.residenceAddress")) {
      let addressData = pickAndConvert(formData.secondClientLegalRepresentative.residenceAddress, fields["secondClientLegalRepresentative.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);

      if (hasChangedData(addressData, product.secondClientLegalRepresentative.residenceAddress)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.secondClientLegalRepresentative.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "authorityAddress")) {
      let addressData = _pick(convertedFormData.authorityAddress, fields.authorityAddress);
      addressData = sanitizeAddressData(addressData);

      if (hasChangedData(addressData, product.authorityAddress)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.authorityAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "caseInsurance")) {
      const convertedFormData = this.getConvertedFormData(formData);
      let caseInsuranceData = pickAndConvert(convertedFormData.caseInsurance, fields["caseInsurance"]);
      await ApiClient.patch("/case_insurances/" + product.caseInsurance.id, { body: JSON.stringify(caseInsuranceData) });
    }

    if (_has(fields, "caseInsurance.policyHolder")) {
      const basePolicyHolderData = formData.caseInsurance.policyHolderType === "me" ? formData.customer.person : formData.caseInsurance.policyHolder;
      let policyHolderData = pickAndConvert(basePolicyHolderData, fields["caseInsurance.policyHolder"]);
      policyHolderData = sanitizePersonData(policyHolderData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.caseInsurance.policyHolder.id,
        data: policyHolderData,
      });
    }

    let productData: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      productData.applicationStatus = nextApplicationStatus;
      this.statusFunctions[applicationStatus]?.();
    }

    if (_has(fields, product.name)) {
      productData = _merge(productData, pickAndConvert(formData, fields[product.name]), pickAndConvert(formData, fields.product));
      productData = this.getConvertedFormData(productData);
    }
    if (hasCustomerUpdates) {
      await refreshCustomer();
    }

    if (nextApplicationStatus === applicationStatuses.APPLICATION_STATUS_REFINEMENT) {
      productData["generationRequest"] = "initialAssessment";
    } else if (nextApplicationStatus === applicationStatuses.APPLICATION_STATUS_CONCERN_SUMMARY) {
      productData["generationRequest"] = "finalAssessment";
    }

    productData["clearGenerationFields"] = clearGenerationFields;

    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: productData,
    });
  };

  getConvertedFormData = (data: Partial<AbstractCase>) => {
    return getConvertedFormDataBase(data, this.conversionFields);
  };

  getFrontendOnlyFieldsInitialValues = (product: AbstractCase) => ({
    howManyClients: product.additionalClients?.length ? "twoClients" : "",
  });

  prepareFormData = (product: AbstractCase, customer: Customer) => {
    let formData = prepareFormDataBase(product, customer, this.conversionFields);
    return { ...this.getFrontendOnlyFieldsInitialValues(product), ...formData };
  };

  getAvailableSteps = (formData: Record<string, any>, product: AbstractCase) => {
    let availableSteps = [];

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_BASE]);

    if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory)) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_EMPLOYMENT_RELATIONSHIP]);
    }

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_REFINEMENT]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONCERN_SUMMARY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INSURANCE]);

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
    //for dependencies that are changed in the same step as we are we need to check product instead of formdata to not
    // invalidate our next step since formdata updates before the actual request is fired
    if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory) && !product.powerOfAttorney) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
    }
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);

    return availableSteps;
  };
}

export default new FormService();
